import React, { useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { ReactComponent as CloseIcon } from "../../../Assets/Icons/CloseIconSvg.svg";
import uploadIcon from "../../../Assets/Icons/uploadOutlineIcon.svg";
import deleteIcon from "../../../Assets/Icons/deleteIcon.svg";
import { updateMerchantBusinessProfile } from "../../../services/merchantService";
import { uploadFile } from "../../../utils/s3";

const ChangeLogoModal = ({ merchantDetails, onClose, onSuccess }) => {
    const dispatch = useDispatch();
    const fileInputRef = useRef(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState("");

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
            if (!validTypes.includes(file.type)) {
                setError("Please select a valid image file (JPEG, PNG, or WebP)");
                return;
            }

            // Validate file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                setError("File size must be less than 5MB");
                return;
            }

            setError("");
            setSelectedFile(file);

            // Create preview URL
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewUrl(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleRemoveFile = () => {
        setSelectedFile(null);
        setPreviewUrl(null);
        setError("");
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleSubmit = async () => {
        if (!selectedFile) {
            setError("Please select a logo to upload");
            return;
        }

        setUploading(true);
        setError("");

        try {
            // 1. Upload to S3 first
            // Note: uploadFile returns the filename (key), not the full URL, consistent with GettingStarted flow
            const s3LogoName = await uploadFile(selectedFile, selectedFile.name);

            if (!s3LogoName) {
                throw new Error("Failed to upload image to S3");
            }

            // 2. Send the filename/URL to backend via existing API
            // The backend expects a JSON payload with the 'logo' field
            const response = await dispatch(
                updateMerchantBusinessProfile({
                    id: merchantDetails._id,
                    data: { logo: s3LogoName }
                })
            ).unwrap();

            if (response?.success) {
                onSuccess && onSuccess();
                onClose();
            } else {
                setError(response?.message || "Failed to update logo");
            }
        } catch (err) {
            console.error("Error updating logo:", err);
            setError(err?.message || "Failed to update logo. Please try again.");
        } finally {
            setUploading(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-[10000] flex items-center justify-center bg-black bg-opacity-50"
            style={{ animation: "fadeIn 0.25s cubic-bezier(0.16, 1, 0.3, 1)" }}
        >
            <style>
                {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideIn {
            from { transform: translateY(-20px) scale(0.95); opacity: 0; }
            to { transform: translateY(0) scale(1); opacity: 1; }
          }
        `}
            </style>

            <div
                className="bg-white rounded-[12px] p-6 w-[90%] max-w-[500px] shadow-xl relative"
                style={{ animation: "slideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)" }}
            >
                {/* Close button inside modal at top-right corner */}
                <button
                    onClick={onClose}
                    className="absolute -top-4 -right-4 w-[32px] h-[32px] flex items-center justify-center z-10"
                >
                    <CloseIcon className="w-full h-full" />
                </button>

                {/* Header */}
                <div className="flex justify-between items-start mb-6">
                    <h2 className="text-[20px] font-[600] text-[#303030] font-poppins">
                        Change Merchant Logo
                    </h2>
                </div>

                {/* Merchant Info */}
                <div className="mb-6">
                    <p className="text-[14px] font-[500] text-[#767676] mb-1">
                        Merchant Name
                    </p>
                    <p className="text-[16px] font-[600] text-[#303030]">
                        {merchantDetails?.businessName || "N/A"}
                    </p>
                </div>

                {/* Current Logo */}
                <div className="mb-6">
                    <p className="text-[14px] font-[500] text-[#303030] mb-3">
                        Current Logo
                    </p>
                    <div className="flex justify-center">
                        {merchantDetails?.logo ? (
                            <img
                                src={
                                    merchantDetails.logo.startsWith("http") || merchantDetails.logo.startsWith("data:")
                                        ? merchantDetails.logo
                                        : `${process.env.REACT_APP_S3_URL || ""}${merchantDetails.logo}`
                                }
                                alt="Current Logo"
                                className="w-[100px] h-[100px] object-cover rounded-full border-2 border-[#00ADEF33]"
                            />
                        ) : (
                            <div className="w-[100px] h-[100px] rounded-full bg-[#E6F7FF] border-2 border-[#00ADEF33] flex items-center justify-center">
                                <span className="text-[#00ADEF] text-[14px] font-[500]">
                                    No Logo
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Upload Section */}
                <div className="mb-6">
                    <p className="text-[14px] font-[500] text-[#303030] mb-3">
                        Upload New Logo
                    </p>

                    {!selectedFile ? (
                        <div
                            onClick={handleUploadClick}
                            className="border-2 border-dashed border-[#00ADEF] rounded-[8px] p-8 flex flex-col items-center justify-center cursor-pointer hover:bg-[#F5F5F5] transition-colors"
                        >
                            <img src={uploadIcon} alt="Upload" className="w-[48px] h-[48px] mb-3" />
                            <p className="text-[14px] font-[500] text-[#00ADEF] mb-1">
                                Click to upload
                            </p>
                            <p className="text-[12px] font-[400] text-[#767676]">
                                JPEG, PNG, or WebP (Max 5MB)
                            </p>
                        </div>
                    ) : (
                        <div className="border-2 border-[#00ADEF] rounded-[8px] p-4">
                            <div className="flex items-center gap-4">
                                <img
                                    src={previewUrl}
                                    alt="Preview"
                                    className="w-[60px] h-[60px] object-cover rounded-full flex-shrink-0"
                                />
                                <div className="flex-1 min-w-0">
                                    <p className="text-[14px] font-[500] text-[#303030] truncate">
                                        {selectedFile.name}
                                    </p>
                                    <p className="text-[12px] font-[400] text-[#767676]">
                                        {(selectedFile.size / 1024).toFixed(2)} KB
                                    </p>
                                </div>
                                <button
                                    onClick={handleRemoveFile}
                                    className="w-[32px] h-[32px] flex items-center justify-center hover:bg-[#FFF1F0] rounded-[6px] transition-colors flex-shrink-0"
                                    title="Remove file"
                                >
                                    <img src={deleteIcon} alt="Delete" className="w-[18px] h-[18px]" />
                                </button>
                            </div>
                        </div>
                    )}

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        onChange={handleFileSelect}
                        className="hidden"
                    />
                </div>

                {/* Error Message */}
                {error && (
                    <div className="mb-4 p-3 bg-[#FFF1F0] border border-[#FFCCC7] rounded-[6px]">
                        <p className="text-[13px] font-[400] text-[#FF4D4F]">{error}</p>
                    </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 justify-end">
                    <button
                        onClick={onClose}
                        disabled={uploading}
                        className="px-6 py-2 border border-[#D9D9D9] rounded-[6px] text-[14px] font-[500] text-[#303030] hover:bg-[#F5F5F5] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={uploading || !selectedFile}
                        className="px-6 py-2 bg-[#00ADEF] rounded-[6px] text-[14px] font-[500] text-white hover:bg-[#0088CC] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {uploading ? (
                            <>
                                <div className="w-[16px] h-[16px] border-2 border-white border-t-transparent rounded-full animate-spin" />
                                Uploading...
                            </>
                        ) : (
                            "Update Logo"
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ChangeLogoModal;
